import { FC, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import { useFlag } from "@unleash/proxy-client-react";
import useMediaQuery from "@mui/material/useMediaQuery";

import { RootState } from "@store/rootReducer";
import { useGetUserDetailsQuery } from "@store/services/user";
import { setDrawerOpen } from "@store/slices/appSlice";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import GlobalStyles from "@mui/material/GlobalStyles";
import { PageDrawerContext } from "@components/Page";
import ErrorBoundary from "@components/ErrorBoundary";
import FlashMessage from "@features/FlashMessage";
import Modals from "@features/Modals";
import OpenPdfFileModal from "@features/OpenPdfFileModal/OpenPdfFileModal";
import SharedEmailCheck from "@features/SharedEmailCheck";
import CartSidebar from "./components/CartSidebar";
import Drawer from "./components/Drawer";
import Footer from "./components/Footer";
import Header from "./components/Header";

import { brandColorTokens } from "@constants";
import { appLayoutDimensions } from "./AppLayout.constants";

const AppLayout: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const isSystemMessage = useFlag("SystemMessage");
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"), { noSsr: true });

  const { data: userDetails } = useGetUserDetailsQuery();
  const isDesktopDrawerOpen = useSelector((state: RootState) => state.app.isDrawerOpen);
  const isDrawerVisible = useSelector((state: RootState) => state.app.isDrawerVisible);
  const isHeaderAuthMenuVisible = useSelector((state: RootState) => state.app.isHeaderAuthMenuVisible);

  const isAuthenticated = userDetails?.authenticated === true;
  const isDrawerShown = isAuthenticated && isDrawerVisible;
  const isDrawerOpen = isDesktop ? isDesktopDrawerOpen : isMobileDrawerOpen;
  const isAccountNavigationShown = isAuthenticated && isHeaderAuthMenuVisible;

  useEffect(() => {
    document.documentElement.scrollTo({ top: 0, left: 0 });
  }, [pathname]);

  const handleDrawerToggle = () => {
    if (isDesktop) {
      dispatch(setDrawerOpen(!isDesktopDrawerOpen));
      return;
    }

    setIsMobileDrawerOpen(true);
  };

  const handleMobileDrawerClose = () => setIsMobileDrawerOpen(false);

  return (
    <>
      <Modals />
      <ErrorBoundary hideErrorMessage={true}>
        <FlashMessage />
      </ErrorBoundary>
      <OpenPdfFileModal />
      <SharedEmailCheck />
      <Box
        data-cy="app-layout"
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100dvh",
          bgcolor: brandColorTokens.background.primary,
        }}
      >
        <GlobalStyles
          styles={(theme) => ({ "html body": { fontFamily: `${theme.typography.fontFamily} !important` } })}
        />
        {isSystemMessage && (
          <Alert severity="error" sx={{ borderRadius: 0, justifyContent: "center" }}>
            {t("systemMessages.infoHeaderMessage")}
          </Alert>
        )}
        <Header
          isMenuButtonShown={isDrawerShown}
          isAccountNavigationShown={isAccountNavigationShown}
          isHiddenOnScroll={!isDesktop}
          onMenuToggle={handleDrawerToggle}
        />
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            minHeight: `calc(100dvh - ${appLayoutDimensions.headerHeight}px)`,
          }}
        >
          {isDrawerShown && <Drawer isDesktop={isDesktop} isOpen={isDrawerOpen} onClose={handleMobileDrawerClose} />}
          <Box component="main" sx={{ flexGrow: 1, minWidth: 0 }}>
            <PageDrawerContext value={isDrawerShown}>
              <Outlet />
            </PageDrawerContext>
          </Box>
        </Box>
        <Footer />
        <CartSidebar />
      </Box>
    </>
  );
};

export default AppLayout;
